'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { municipalities, comarques, getMunicipalityById, getComarcaByMunicipalityId } from '@/data/territorialCatalog';
import type { Municipality, Comarca, Workspace } from '@/data/territorialCatalog';
import { organizations, getDefaultOrganization, getNeutralOrganization, getOrgMunicipalityConfig } from '@/data/organizationData';
import type { Organization, OrganizationMunicipalityConfig } from '@/data/organizationData';
import { initializeDemoState, isDemoStoreHydrated, getWorkspaces, addWorkspaceToStore, subscribeDemoState,  } from '@/services/demoStore/index';
import type { DemoWorkspace, DemoState } from '@/services/demoStore/schema';

// Storage keys (kept for environment/municipality selection — NOT for workspaces)
const STORAGE_KEY = 'plenaricat_active_municipality_id';
const LEGACY_KEY = 'ple_active_municipality_id';
const ACTIVE_ENV_KEY = 'plenaricat_active_environment';
const REMEMBER_ENV_KEY = 'plenaricat_remember_env';

export type ActiveEnvironment = 'plenaricat-general' | 'alianca-catalana';

function migrateAndRead(): string | null {
  try {
    const newVal = localStorage.getItem(STORAGE_KEY);
    if (newVal) return newVal;
    const oldVal = localStorage.getItem(LEGACY_KEY);
    if (oldVal) {
      localStorage.setItem(STORAGE_KEY, oldVal);
      localStorage.removeItem(LEGACY_KEY);
      return oldVal;
    }
  } catch {
    // SSR guard
  }
  return null;
}

/**
 * Reads the persisted environment from sessionStorage first, then localStorage.
 * Returns null if no valid value is found — never falls back to a default.
 */
function readActiveEnvironment(): ActiveEnvironment | null {
  try {
    const ss = sessionStorage.getItem(ACTIVE_ENV_KEY);
    if (ss === 'plenaricat-general' || ss === 'alianca-catalana') return ss;
    const ls = localStorage.getItem(ACTIVE_ENV_KEY);
    if (ls === 'plenaricat-general' || ls === 'alianca-catalana') return ls;
  } catch {
    // SSR guard
  }
  return null;
}

// Convert DemoWorkspace to legacy Workspace shape for backward compat
function demoWorkspaceToWorkspace(dw: DemoWorkspace): Workspace {
  return {
    id: dw.id,
    municipalityId: dw.municipalityId,
    nom: dw.nom,
    rolUsuari: dw.rolUsuari,
    actiu: dw.actiu,
    creatA: dw.creatA,
  };
}

export interface WorkspaceContextValue {
  // Active state
  activeEnvironment: ActiveEnvironment | null;
  isEnvironmentHydrated: boolean;
  activeOrganization: Organization | null;
  activeWorkspace: Workspace | null;
  activeMunicipality: Municipality | null;
  activeComarca: Comarca | null;
  organizationMunicipalityConfig: OrganizationMunicipalityConfig | null;
  // Available data
  availableWorkspaces: Workspace[];
  availableMunicipalities: Municipality[];
  availableComarques: Comarca[];
  availableOrganizations: Organization[];
  // Loading
  isLoading: boolean;
  // Actions
  setActiveWorkspace: (municipalityId: string) => void;
  clearActiveWorkspace: () => void;
  createWorkspace: (municipalityId: string) => Workspace;
  setActiveEnvironment: (env: ActiveEnvironment, remember?: boolean) => void;
  // Legacy compat
  selectMunicipality: (id: string) => void;
  clearMunicipality: () => void;
  municipalities: Municipality[];
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [activeMunicipalityId, setActiveMunicipalityId] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Start as null — never assume an environment before hydration
  const [activeEnvironment, setActiveEnvironmentState] = useState<ActiveEnvironment | null>(null);
  const [isEnvironmentHydrated, setIsEnvironmentHydrated] = useState(false);

  useEffect(() => {
    try {
      // Initialize demoStore (first load: seeds from mocks; subsequent: loads from localStorage)
      initializeDemoState();

      const storedId = migrateAndRead();
      const env = readActiveEnvironment();

      // Load workspaces from demoStore
      const demoWorkspaces = getWorkspaces();
      const legacyWorkspaces = demoWorkspaces.map(demoWorkspaceToWorkspace);
      setWorkspaces(legacyWorkspaces);

      // Only set environment if a valid persisted value exists
      if (env !== null) {
        setActiveEnvironmentState(env);
      }
      if (storedId) {
        setActiveMunicipalityId(storedId);
      }
    } catch {
      // localStorage not available
    } finally {
      setIsEnvironmentHydrated(true);
      setIsLoading(false);
    }

    // Subscribe to demoStore changes to keep workspaces in sync
    const unsubscribe = subscribeDemoState((state: DemoState) => {
      const updated = state.workspaces.map(demoWorkspaceToWorkspace);
      setWorkspaces(updated);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Derive active organization from environment
  const activeOrganization: Organization | null = activeEnvironment === 'plenaricat-general'
    ? getNeutralOrganization()
    : activeEnvironment === 'alianca-catalana'
      ? getDefaultOrganization()
      : null;

  const activeMunicipality = activeMunicipalityId
    ? getMunicipalityById(activeMunicipalityId) ?? null
    : null;

  const activeComarca = activeMunicipality
    ? getComarcaByMunicipalityId(activeMunicipality.id) ?? null
    : null;

  const activeWorkspace = useMemo(() => {
    if (!activeMunicipalityId) return null;
    // Prefer workspace matching both municipalityId AND organizationId for isolation
    if (activeOrganization && isDemoStoreHydrated()) {
      const demoWs = getWorkspaces();
      const matched = demoWs.find(
        (w) => w.municipalityId === activeMunicipalityId && w.organizationId === activeOrganization.id
      );
      if (matched) return demoWorkspaceToWorkspace(matched);
    }
    return workspaces.find((w) => w.municipalityId === activeMunicipalityId) ?? null;
  }, [activeMunicipalityId, activeOrganization, workspaces]);

  // Derive OrganizationMunicipalityConfig for active municipality
  const organizationMunicipalityConfig = activeMunicipalityId && activeOrganization
    ? getOrgMunicipalityConfig(activeOrganization.id, activeMunicipalityId)
    : null;

  const setActiveEnvironment = useCallback((env: ActiveEnvironment, remember?: boolean) => {
    setActiveEnvironmentState(env);
    try {
      // Always write to sessionStorage for current session
      sessionStorage.setItem(ACTIVE_ENV_KEY, env);
      if (remember) {
        // Persist across sessions
        localStorage.setItem(ACTIVE_ENV_KEY, env);
        localStorage.setItem(REMEMBER_ENV_KEY, 'true');
      } else {
        // Clear any previous localStorage value so it doesn't override the new selection
        localStorage.removeItem(ACTIVE_ENV_KEY);
        localStorage.removeItem(REMEMBER_ENV_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  const setActiveWorkspace = useCallback((municipalityId: string) => {
    setActiveMunicipalityId(municipalityId);
    try {
      localStorage.setItem(STORAGE_KEY, municipalityId);
      localStorage.removeItem(LEGACY_KEY);
    } catch {
      // ignore
    }
  }, []);

  const clearActiveWorkspace = useCallback(() => {
    setActiveMunicipalityId(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_KEY);
    } catch {
      // ignore
    }
  }, []);

  const createWorkspace = useCallback((municipalityId: string): Workspace => {
    const muni = getMunicipalityById(municipalityId);
    const orgId = activeEnvironment === 'alianca-catalana' ?'org-alianca-catalana' :'org-plenaricat-general';

    // Check for duplicate: same organizationId + municipalityId
    if (isDemoStoreHydrated()) {
      const existing = getWorkspaces().find(
        (w: DemoWorkspace) => w.municipalityId === municipalityId && w.organizationId === orgId
      );
      if (existing) {
        return demoWorkspaceToWorkspace(existing);
      }
    }

    // Use org-scoped ID to ensure isolation
    const orgSuffix = activeEnvironment === 'alianca-catalana' ? 'ac' : 'general';
    const newDemoWs: DemoWorkspace = {
      id: `ws-${municipalityId}-${orgSuffix}-${Date.now()}`,
      municipalityId,
      organizationId: orgId,
      nom: muni?.nom ?? municipalityId,
      rolUsuari: 'Regidor/a',
      actiu: true,
      creatA: new Date().toISOString().split('T')[0],
    };

    // Persist to demoStore
    addWorkspaceToStore(newDemoWs);

    const legacyWs = demoWorkspaceToWorkspace(newDemoWs);
    setWorkspaces((prev) => [...prev, legacyWs]);
    return legacyWs;
  }, [activeEnvironment]);

  // Legacy compat aliases
  const selectMunicipality = setActiveWorkspace;
  const clearMunicipality = clearActiveWorkspace;

  return (
    <WorkspaceContext.Provider
      value={{
        activeEnvironment,
        isEnvironmentHydrated,
        activeOrganization,
        activeWorkspace,
        activeMunicipality,
        activeComarca,
        organizationMunicipalityConfig,
        availableWorkspaces: workspaces,
        availableMunicipalities: municipalities,
        availableComarques: comarques,
        availableOrganizations: organizations,
        isLoading,
        setActiveWorkspace,
        clearActiveWorkspace,
        createWorkspace,
        setActiveEnvironment,
        selectMunicipality,
        clearMunicipality,
        municipalities,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return ctx;
}
